import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_PROFILE_STAGE_KEYS, INCOME_SOURCE_MANDATORY_KEYS } from '../../common/constants';
import { isStageDisabled } from '../../common/helper';
import { useAppSelector } from '../../common/hooks';
import { IncomeSourcesType } from '../../common/types';
import { IconButton, Typography, BasicButton } from '../../components/atoms';
import { ConfirmationCentered } from '../../components/molecules';
import AddIncomeSourcesDrawer from '../add-income-sources-drawer/index';
import { ReactComponent as CashIcon } from '../../assets/icons/cash.svg';
import { HeaderIcon } from './HeaderIcon';
import { Card } from '../../components/molecules/Card/Card';
import {
    formatCurrency,
    getDurationColor,
    getDurationLabelFromAccountType,
    getLabelFromAccountType
} from '../../utils';
import { ReactComponent as EditIcon } from '../../assets/icons/editIcon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/deleteIcon.svg';
import { Box, Chip, useTheme } from '@mui/material';
import { TableWithCustomRender } from '../../components/molecules/TableWithCustomRender';
import Dayjs from 'dayjs';
import { isRecordDataValid } from '../../features/advisorConsole/utils';

export function calculateTotalIncome(incomeSources: IncomeSourcesType[]) {
    let annualamount = 0;
    incomeSources.forEach((item: any) => {
        annualamount += +(item.frequency === 'Annually' ? item.amount : item.amount * 12);
    });
    return annualamount;
}

export const IncomeCard = () => {
    const [isAddIncomeSourceDrawerOpen, setIsAddIncomeSourceDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const language = i18n.language;
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const { orgInfo } = useAppSelector((state) => state.global);
    const { incomeSources } = adviceClient.accounts;
    const [deleteModalState, setDeleteModalState] = useState(false);
    const [needPrepopulationOnEdit, setNeedPrepopulationOnEdit] = useState(true);
    const [selectedAccountForDelete, setSelectedAccountForDelete] = useState<string>('');
    const [triggerDelete, setTriggerDelete] = useState<string>('');
    const incomeSourceType = orgInfo?.incomeSourceType?.[language] || [];

    // eslint-disable-next-line
    let amountIncomeSource = 0;
    incomeSources.forEach((item: any) => {
        amountIncomeSource += item.frequency === 'Annually' ? item.amount : item.amount * 12;
    });
    const isDisabled = isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[2]);

    const headers_ff = [
        {
            label: t('NAME'),
            key: 'sourceName',
            align: 'left' as const,
            width: '20%',
            render: (value: string) => (
                <Typography label={value} color="neutral.grey.500" variant="body.large" sx={{ textAlign: 'left' }} />
            )
        },
        {
            label: t('OWNER'),
            key: 'sourceOwner',
            align: 'left' as const,
            width: '11%',
            render: (value: string) => (
                <Typography
                    label={value === 'primary' ? t('CLIENT') : value === 'joint' ? t('TEXT_JOINT') : t('CO-CLIENT')}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'left' }}
                />
            )
        },
        {
            label: t('DURATION'),
            key: 'duration',
            align: 'center' as const,
            width: '11%',
            sortable: true,

            // onSortChange: (_key: string, dir: 'asc' | 'desc') => { setOrder(dir); setOrderBy('duration'); },
            sortEquation: (a: any, b: any, order: 'asc' | 'desc') => {
                const durationA = getDurationLabelFromAccountType(a.sourceType, incomeSourceType);
                const durationB = getDurationLabelFromAccountType(b.sourceType, incomeSourceType);
                if (durationA < durationB) return order === 'asc' ? -1 : 1;
                if (durationA > durationB) return order === 'asc' ? 1 : -1;
                return 0;
            },
            render: (_value: any, row: any) => {
                console.log('Rendering duration for row: ', row.sourceType, row);
                const duration = getDurationLabelFromAccountType(row.sourceType, incomeSourceType);
                return (
                    <Typography
                        label={
                            <Chip
                                label={t(duration.split(' ')[0].toUpperCase())}
                                size="small"
                                sx={{
                                    backgroundColor: getDurationColor(theme, duration),
                                    fontWeight: 600,
                                    minWidth: '100px',
                                    borderRadius: '16px'
                                }}
                            />
                        }
                        variant="body.large"
                        color="neutral.grey.500"
                        sx={{ textAlign: 'left', lineHeight: '20px' }}
                    />
                );
            }
        },
        {
            label: t('TYPE'),
            key: 'sourceType',
            align: 'center' as const,
            width: '16%',
            sortable: true,
            // onSortChange: (_key: string, dir: 'asc' | 'desc') => { setOrder(dir); setOrderBy('sourceType'); },
            render: (value: string) => (
                <Typography
                    label={getLabelFromAccountType(value, incomeSourceType)}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'center', lineHeight: '20px' }}
                />
            )
        },
        {
            label: t('LABELS_MONTHLY_INCOME'),
            key: 'amount',
            align: 'center' as const,
            width: '16%',
            render: (value: number, row: any) => (
                <Typography
                    label={formatCurrency(row.frequency === t('MONTHLY') ? value : Math.round(value! / 12))}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'center', lineHeight: '20px' }}
                />
            )
        },
        {
            label: t('FREQUENCY'),
            key: 'frequency',
            align: 'left' as const,
            width: '11%',
            render: (value: string) => (
                <Typography
                    label={t(value === t('ANNUALLY') ? 'ANNUAL' : 'MONTHLY')}
                    variant="body.large"
                    color="neutral.grey.500"
                    sx={{ textAlign: 'left', lineHeight: '20px' }}
                />
            )
        },
        {
            label: `${t('BEGINING_YEAR')}/${t('ENDING')}`,
            key: 'dateRange',
            align: 'center' as const,
            width: '26%',
            render: (_value: any, row: any) => {
                const { beginningYear, endYear } = row;
                let startDate = null;
                if (beginningYear) {
                    startDate = Dayjs(beginningYear, 'DD-MM-YYYY', true);
                    if (!startDate.isValid()) startDate = Dayjs(beginningYear);
                    if (!startDate.isValid()) startDate = null;
                }
                let endDate = null;
                if (endYear) {
                    endDate = Dayjs(endYear, 'DD-MM-YYYY', true);
                    if (!endDate.isValid()) endDate = Dayjs(endYear);
                    if (!endDate.isValid()) endDate = null;
                }
                const label = (() => {
                    if (!startDate && !endDate) return '-';
                    if (startDate && !endDate) return startDate.format('MMM YYYY');
                    if (!startDate && endDate) return endDate.format('MMM YYYY');
                    return `${startDate!.format('MMM YYYY')} -\n${endDate!.format('MMM YYYY')}  `;
                })();
                return (
                    <Typography
                        label={label}
                        color="neutral.grey.500"
                        variant="body.large"
                        sx={{ textAlign: 'center', lineHeight: '20px', paddingLeft: '13px' }}
                    />
                );
            }
        },
        {
            label: t('EDIT'),
            key: 'edit',
            align: 'center' as const,
            width: '14%',
            render: (_value: any, row: any) => (
                <Box display="flex" gap={1} justifyContent="center">
                    <IconButton
                        iconName={<EditIcon color="primary" />}
                        bordered={false}
                        onClick={() => handleEditIncomeSource(row.accountId)}
                        color="primary"
                    />
                    <IconButton
                        iconName={<DeleteIcon color="primary" />}
                        bordered={false}
                        onClick={() => {
                            setSelectedAccountForDelete(row.accountId);
                            setDeleteModalState(true);
                        }}
                        color="primary"
                    />
                </Box>
            )
        }
    ];

    const handleAddIncomeSource = () => {
        setSelectedAccountId('');
        setDrawerMode('add');
        setIsAddIncomeSourceDrawerOpen(true);
    };

    const handleEditIncomeSource = (accountId: string) => {
        setDrawerMode('edit');
        setSelectedAccountId(accountId);
        setIsAddIncomeSourceDrawerOpen(true);
        setNeedPrepopulationOnEdit(false);
    };

    return (
        <Card onClick={() => {}}>
            <div className="goe-client-stepper-card-inner">
                <div className="goe-client-card-header-container">
                    <div className="goe-client-card-header">
                        <div className="goe-client-icon">
                            <HeaderIcon isDisabled={false} size={40} icon={<CashIcon />} />
                        </div>
                        <div className="goe-client-card-header-content">
                            <Typography variant="headers.h4" color="semantic.text.default" label={t('LABEL_INCOME')} />
                            <Typography
                                label={t('INCOME_SOURCES_INFO_TEXT')}
                                color="semantic.text.muted"
                                variant="body.small"
                            />
                        </div>
                    </div>
                    <div className="goe-client-card-header-buttons">
                        <BasicButton
                            disabled={isDisabled}
                            variant="outlined"
                            sx={{ marginRight: '8px' }}
                            onClick={handleAddIncomeSource}
                        >
                            {t('TEXT_ADD_INCOME')}
                        </BasicButton>
                    </div>
                </div>
                {incomeSources.length > 0 && (
                    <TableWithCustomRender
                        headers={headers_ff}
                        rowHeight="56px"
                        dataSource={incomeSources ?? []}
                        conditionalRowFormat={(row) => {
                            const isValid = isRecordDataValid(row, INCOME_SOURCE_MANDATORY_KEYS, true, false);
                            return !isValid
                                ? {
                                      '&.MuiTableRow-root': {
                                          boxShadow: (t: any) => `inset 0 0 0 2px ${t.palette.error[500]}`
                                      }
                                  }
                                : undefined;
                        }}
                    />
                )}
            </div>
            <ConfirmationCentered
                onCancellation={() => setDeleteModalState(false)}
                onCloseIconClick={() => setDeleteModalState(false)}
                onConfirmation={() => {
                    setDeleteModalState(false);
                    setTriggerDelete(selectedAccountForDelete);
                    setSelectedAccountForDelete('');
                }}
                open={deleteModalState}
                content={`${t('DELETE_CONFIRMATION_MODAL_CONTENT')}`}
                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                title={t('DELETE_CONFIRMATION_MODAL_HEADER_TEXT')}
                width="396px"
            />
            <AddIncomeSourcesDrawer
                setOpenDrawer={setIsAddIncomeSourceDrawerOpen}
                openDrawer={isAddIncomeSourceDrawerOpen}
                mode={drawerMode}
                selectedAccountId={selectedAccountId}
                triggerDeleteAccountId={triggerDelete}
                onDeleteTriggered={() => setTriggerDelete('')}
                needPrepopulationOnEdit={needPrepopulationOnEdit}
            />
        </Card>
    );
};
